import type { Locale } from '@/intl/types'
import type { GroupBookingData } from '@/scopes/account/group-responsible/domain/types'
import type { GroupBookingAPIResponse } from '@/scopes/account/group-responsible/infrastructure/types'
import defaultFetch from 'isomorphic-unfetch'
import { groupBookingMapper } from './mappers/groupResponsibleMapper'

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string
  }
  fetch: typeof defaultFetch
}

interface AssignBusPayload {
  masterBookingNumber: string
  participantIds: string[]
  pickupPointId: string
  returnPointId: string
}

interface AssignBusResponse {
  success: boolean
}

function createGroupResponsibleAPIClient(dependencies: Partial<Dependencies> = {}) {
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    fetch = defaultFetch,
  } = dependencies as Dependencies

  async function getGroupBookingData(masterBookingNumber: string, locale?: Locale): Promise<GroupBookingData> {
    const response = await fetch(
      `${env.NEXT_PUBLIC_API_BASE_URL}/GroupBookingsApi/GetGroupBooking/?masterBookingNumber=${masterBookingNumber}`,
    )
    const data: GroupBookingAPIResponse = await response.json()
    return groupBookingMapper(data, locale)
  }

  async function assignBusToParticipants(payload: AssignBusPayload): Promise<AssignBusResponse> {
    const response = await fetch(
      `${env.NEXT_PUBLIC_API_BASE_URL}/GroupBookingsApi/AssignBus`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    )
    if (!response.ok) {
      throw new Error('Failed to assign bus to participants')
    }
    return response.json()
  }

  return {
    getGroupBookingData,
    assignBusToParticipants,
  }
}

export default createGroupResponsibleAPIClient()
